// Fully reusable component in `FormRadio` and `FormCheckbox`
// therefore no need for duplicated tests and stories

import { IFormSelector } from '@models/IFormSelector';
import SizeEnum from '@models/enums/SizeEnum';
import classNames from 'classnames';
import { ErrorMessage, useField } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { ChangeEvent, useRef } from 'react';
import { isIOS } from 'react-device-detect';

const FormSelector = ({
  dataTestId = 'Form-Selector',
  id,
  label,
  labelType = SizeEnum.Small,
  name,
  showError = true,
  type,
  value,
  isCentered = true,
  isRichTextLabel,
  checkboxSize = SizeEnum.Small,
  checkboxBorderColor = 'border-grey-500',
  ...rest
}: IFormSelector) => {
  const [{ checked, onChange, ...restField }, { error, touched }] = useField({
    name,
    type,
    value,
  });
  const fieldId = id || (value ? `${name}-${value}` : name);
  const ref = useRef<HTMLInputElement>(null);

  const handleOnChange = (ev: ChangeEvent<HTMLInputElement>) => {
    if (isIOS) ref.current?.focus();

    onChange(ev);
  };

  const isError = !!error && touched;

  return (
    <div data-testid={dataTestId}>
      <div
        className={classNames('relative flex', {
          'items-center': isCentered,
        })}
        data-testid="form-selector-item"
      >
        <input
          {...rest}
          {...restField}
          checked={checked}
          className={classNames(
            'absolute z-max appearance-none opacity-0 focus:opacity-100',
            {
              'h-[15px] w-[15px]': checkboxSize === SizeEnum.Small,
              'h-5 w-5': checkboxSize === SizeEnum.Large,
            }
          )}
          id={fieldId}
          onChange={handleOnChange}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();

              const target = event.target as HTMLInputElement;
              target.click();
            }
          }}
          ref={ref}
          type={type}
        />

        <div
          className={classNames(
            'flex flex-shrink-0 items-center justify-center border bg-white',
            type === 'checkbox' ? 'rounded-sm' : 'rounded-full',
            isError ? 'border-red' : checkboxBorderColor,
            {
              'h-[15px] w-[15px]': checkboxSize === SizeEnum.Small,
              'h-5 w-5': checkboxSize === SizeEnum.Large,
            }
          )}
        >
          <AnimatePresence>
            {checked && (
              <motion.div
                animate={{ opacity: 1 }}
                className={classNames(
                  'bg-black-100',
                  type === 'checkbox' ? 'rounded-sm' : 'rounded-full',
                  {
                    'h-[7px] w-[7px]': checkboxSize === SizeEnum.Small,
                    'h-[10px] w-[10px]': checkboxSize === SizeEnum.Large,
                  }
                )}
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              />
            )}
          </AnimatePresence>
        </div>

        {label && (
          <label
            className={classNames('select-none', {
              'text-grey-600': !checked,
              'body-four ml-2.5': labelType === SizeEnum.Small,
              'h6 ml-4': labelType === SizeEnum.Large,
            })}
            htmlFor={fieldId}
          >
            {isRichTextLabel ? (
              <span
                className="content-area"
                dangerouslySetInnerHTML={{ __html: label as string }}
              />
            ) : (
              label
            )}
          </label>
        )}
      </div>

      {showError && isError && (
        <ErrorMessage name={name}>
          {(msg: string) => (
            <span
              className="body-four text-red"
              data-testid="form-select-label"
            >
              {msg}
            </span>
          )}
        </ErrorMessage>
      )}
    </div>
  );
};

export default FormSelector;
